import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  OutlineButton,
  Badge,
  FlexContainer,
  Input,
  TextArea,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableCell,
  Pagination,
  PageButton
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { Expense, ReceiptType } from '../../types';
import apiService from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatUtils';
import { FaEye, FaCheck, FaTimes, FaReceipt, FaUser, FaCalendarAlt, FaMoneyBillWave } from 'react-icons/fa';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled(Card)`
  text-align: center;
  padding: 1rem;
  
  .stat-number {
    font-size: 2rem;
    font-weight: 600;
    color: var(--primary-color);
    margin-bottom: 0.5rem;
  }
  
  .stat-label {
    color: var(--dark-gray);
    font-size: 0.9rem;
  }
`;

const ExpenseCard = styled(Card)`
  margin-bottom: 1rem;
  
  .expense-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }
  
  .expense-amount {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--primary-color);
  }
  
  .expense-meta {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
  }
  
  .meta-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .label {
      font-weight: 500;
      color: var(--dark-gray);
      min-width: 100px;
    }
    
    .value {
      color: var(--text-color);
    }
  }
  
  .approval-actions {
    display: flex;
    gap: 1rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid var(--light-gray);
  }
`;

const ApprovalModal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    
    h3 {
      margin: 0;
      color: var(--primary-color);
    }
    
    .close-btn {
      background: none;
      border: none;
      font-size: 1.5rem;
      cursor: pointer;
      color: var(--dark-gray);
      
      &:hover {
        color: var(--text-color);
      }
    }
  }
  
  .form-group {
    margin-bottom: 1rem;
    
    label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 500;
      color: var(--dark-gray);
    }
    
    textarea {
      width: 100%;
      padding: 0.75rem;
      border: 1px solid var(--light-gray);
      border-radius: 6px;
      resize: vertical;
      min-height: 100px;
      
      &:focus {
        outline: none;
        border-color: var(--primary-color);
      }
    }
  }
  
  .modal-actions {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 1.5rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--dark-gray);
  
  .icon {
    font-size: 4rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
`;

const AdminExpenseApproval: React.FC = () => {
  const { user } = useAuth();
  const [pendingExpenses, setPendingExpenses] = useState<Expense[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [approvalModal, setApprovalModal] = useState<{
    isOpen: boolean;
    action: 'approve' | 'reject';
    comments: string;
  }>({
    isOpen: false,
    action: 'approve',
    comments: ''
  });
  
  const itemsPerPage = 10;

  useEffect(() => {
    loadPendingExpenses();
  }, []);

  const loadPendingExpenses = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiService.getPendingExpenses();
      if (response.success) {
        setPendingExpenses(response.data || []);
      } else {
        setError('Failed to load pending expenses');
      }
    } catch (error: any) {
      console.error('Error loading pending expenses:', error);
      setError(error.message || 'Failed to load pending expenses');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproval = async (action: 'approve' | 'reject') => {
    if (!selectedExpense) return;
    
    try {
      const approvalData = {
        action,
        comments: approvalModal.comments
      };
      
      const response = await apiService.approveExpense(selectedExpense._id || selectedExpense.id!, approvalData);
      
      if (response.success) {
        // Remove the approved/rejected expense from the list
        setPendingExpenses(prev => 
          prev.filter(expense => expense._id !== selectedExpense._id && expense.id !== selectedExpense.id)
        );
        
        // Close modal and reset
        setApprovalModal(prev => ({ ...prev, isOpen: false, comments: '' }));
        setSelectedExpense(null);
        
        // Show success message
        alert(`Expense ${action === 'approve' ? 'approved' : 'rejected'} successfully!`);
      }
    } catch (error: any) {
      console.error(`Error ${action}ing expense:`, error);
      alert(`Failed to ${action} expense: ${error.message}`);
    }
  };

  const openApprovalModal = (expense: Expense, action: 'approve' | 'reject') => {
    setSelectedExpense(expense);
    setApprovalModal(prev => ({ ...prev, isOpen: true, action, comments: '' }));
  };

  const closeApprovalModal = () => {
    setApprovalModal(prev => ({ ...prev, isOpen: false, comments: '' }));
    setSelectedExpense(null);
  };

  const getReceiptTypeIcon = (type: ReceiptType) => {
    return type === 'Tax Invoice' ? FaReceipt : FaReceipt;
  };

  const getReceiptTypeColor = (type: ReceiptType) => {
    return type === 'Tax Invoice' ? 'success' : 'warning';
  };

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = pendingExpenses.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(pendingExpenses.length / itemsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: 'red' }}>
        <p>Error: {error}</p>
        <Button onClick={loadPendingExpenses}>Retry</Button>
      </div>
    );
  }

  return (
    <div>
      <PageHeader>
        <h1 className="page-title">Expense Approval</h1>
        <OutlineButton onClick={loadPendingExpenses}>
          Refresh
        </OutlineButton>
      </PageHeader>

      <StatsContainer>
        <StatCard>
          <div className="stat-number">{pendingExpenses.length}</div>
          <div className="stat-label">Pending Expenses</div>
        </StatCard>
        <StatCard>
          <div className="stat-number">
            {formatCurrency(pendingExpenses.reduce((sum, exp) => sum + exp.amount, 0))}
          </div>
          <div className="stat-label">Total Amount Pending</div>
        </StatCard>
      </StatsContainer>

      {pendingExpenses.length === 0 ? (
        <EmptyState>
          <div className="icon">✅</div>
          <h3>No Pending Expenses</h3>
          <p>All expenses have been processed. Great job!</p>
        </EmptyState>
      ) : (
        <>
          {currentItems.map((expense) => (
            <ExpenseCard key={expense._id || expense.id}>
              <CardHeader>
                <div className="expense-header">
                  <div>
                    <h3>{expense.description}</h3>
                    <p>Project: {expense.projectName || 'N/A'}</p>
                  </div>
                  <div className="expense-amount">
                    {formatCurrency(expense.amount, expense.currency)}
                  </div>
                </div>
              </CardHeader>
              
              <CardBody>
                <div className="expense-meta">
                  <div className="meta-item">
                    <span className="label">Receipt Type:</span>
                    <Badge variant={getReceiptTypeColor(expense.receiptType)}>
                      {expense.receiptType}
                    </Badge>
                  </div>
                  
                  <div className="meta-item">
                    <span className="label">Recorded By:</span>
                    <span className="value">
                      {typeof expense.recordedBy === 'string' ? expense.recordedBy : expense.recordedBy.name}
                    </span>
                  </div>
                  
                  <div className="meta-item">
                    <span className="label">Date:</span>
                    <span className="value">{formatDate(new Date(expense.date))}</span>
                  </div>
                  
                  <div className="meta-item">
                    <span className="label">Category:</span>
                    <span className="value">{expense.category}</span>
                  </div>
                </div>
                
                <div className="meta-item">
                  <span className="label">Supplier:</span>
                  <span className="value">{expense.supplier}</span>
                </div>
                
                <div className="meta-item">
                  <span className="label">Receipt Number:</span>
                  <span className="value">{expense.receiptNumber}</span>
                </div>
                
                {expense.notes && (
                  <div className="meta-item">
                    <span className="label">Notes:</span>
                    <span className="value">{expense.notes}</span>
                  </div>
                )}
                
                <div className="approval-actions">
                  <Button 
                    onClick={() => openApprovalModal(expense, 'approve')}
                  >
                    Approve
                  </Button>
                  <OutlineButton 
                    onClick={() => openApprovalModal(expense, 'reject')}
                  >
                    Reject
                  </OutlineButton>
                </div>
              </CardBody>
            </ExpenseCard>
          ))}
          
          {totalPages > 1 && (
            <Pagination>
              <PageButton
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </PageButton>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <PageButton
                  key={page}
                  onClick={() => paginate(page)}
                  active={currentPage === page}
                >
                  {page}
                </PageButton>
              ))}
              
              <PageButton
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </PageButton>
            </Pagination>
          )}
        </>
      )}

      {/* Approval Modal */}
      <ApprovalModal isOpen={approvalModal.isOpen}>
        <ModalContent>
          <div className="modal-header">
            <h3>
              {approvalModal.action === 'approve' ? 'Approve' : 'Reject'} Expense
            </h3>
            <button className="close-btn" onClick={closeApprovalModal}>
              ×
            </button>
          </div>
          
          <div className="form-group">
            <label>
              {approvalModal.action === 'approve' ? 'Approval' : 'Rejection'} Comments:
            </label>
            <textarea
              value={approvalModal.comments}
              onChange={(e) => setApprovalModal(prev => ({ ...prev, comments: e.target.value }))}
              placeholder={`Enter ${approvalModal.action === 'approve' ? 'approval' : 'rejection'} comments...`}
            />
          </div>
          
          <div className="modal-actions">
            <OutlineButton onClick={closeApprovalModal}>
              Cancel
            </OutlineButton>
            <Button
              onClick={() => handleApproval(approvalModal.action)}
            >
              {approvalModal.action === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </div>
        </ModalContent>
      </ApprovalModal>
    </div>
  );
};

export default AdminExpenseApproval;
